import React from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes, useLocation} from 'react-router-dom';
import ConnectionBanner from './components/ConnectionBanner';
import {isTokenValid} from './api';


// Landing
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Footer from './components/Footer';

// Auth
import Login from './pages/Login';
import AppSelector from './pages/AppSelector';
import TenantSelector from './pages/TenantSelector';
import Settings from './pages/Settings';
import OrgAdmin from './pages/OrgAdmin';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminTenantDetail from './admin/pages/AdminTenantDetail';
import NotFound from './pages/NotFound';
import AcceptInvite from './pages/AcceptInvite';
import PrivacyPolicy from './pages/PrivacyPolicy';


// Storage
import Boxes from './storage/pages/Boxes';
import AddBox from './storage/pages/AddBox';
import BoxDetail from './storage/pages/BoxDetail';
import EditBox from './storage/pages/EditBox';

// Inventory
import InventoryDashboard from './inventory/pages/Dashboard';
import Items from './inventory/pages/Items';
import AddItem from './inventory/pages/AddItem';
import ItemDetail from './inventory/pages/ItemDetail';
import EditItem from './inventory/pages/EditItem';
import AddInventory from './inventory/pages/AddInventory';
import EditInventory from './inventory/pages/EditInventory';
import Orders from './inventory/pages/Orders';
import AddOrder from './inventory/pages/AddOrder';
import OrderDetail from './inventory/pages/OrderDetail';
import EditOrder from './inventory/pages/EditOrder';

// Vehicles
import VehiclesList from './vehicles/pages/Vehicles';
import AddVehicle from './vehicles/pages/AddVehicle';
import VehicleDetail from './vehicles/pages/VehicleDetail';
import EditVehicle from './vehicles/pages/EditVehicle';
import AddServiceRecord from './vehicles/pages/AddServiceRecord';
import EditServiceRecord from './vehicles/pages/EditServiceRecord';
import ServiceRecords from './vehicles/pages/ServiceRecords';
import VehicleSettings from './vehicles/pages/Settings';

// Tasks
import TasksList from './tasks/pages/Tasks';
import AddTask from './tasks/pages/AddTask';
import TaskDetail from './tasks/pages/TaskDetail';
import EditTask from './tasks/pages/EditTask';

// CRM
import CrmDashboard from './crm/pages/Dashboard';
import Contacts from './crm/pages/Contacts';
import AddContact from './crm/pages/AddContact';
import ContactDetail from './crm/pages/ContactDetail';
import EditContact from './crm/pages/EditContact';
import Deals from './crm/pages/Deals';
import AddDeal from './crm/pages/AddDeal';
import DealDetail from './crm/pages/DealDetail';
import EditDeal from './crm/pages/EditDeal';
import Companies from './crm/pages/Companies';
import AddCompany from './crm/pages/AddCompany';
import CompanyDetail from './crm/pages/CompanyDetail';
import EditCompany from './crm/pages/EditCompany';
import CrmTasks from './crm/pages/CrmTasks';
import AddCrmTask from './crm/pages/AddCrmTask';
import CrmTaskDetail from './crm/pages/CrmTaskDetail';
import EditCrmTask from './crm/pages/EditCrmTask';
import CrmSettings from './crm/pages/CrmSettings';
import CrmReports from './crm/pages/Reports';

// Marketing
import MarketingDashboard from './marketing/pages/Dashboard';
import MarketingCampaigns from './marketing/pages/Campaigns';
import AddCampaign from './marketing/pages/AddCampaign';
import CampaignDetail from './marketing/pages/CampaignDetail';
import EditCampaign from './marketing/pages/EditCampaign';

// HCM
import HcmDashboard from './hcm/pages/Dashboard';
import AddEmployee from './hcm/pages/AddEmployee';
import EmployeeDetail from './hcm/pages/EmployeeDetail';
import EmployeeDocumentEditor from './hcm/pages/EmployeeDocumentEditor';
import EditEmployee from './hcm/pages/EditEmployee';
import HcmSettings from './hcm/pages/Settings';
import HcmTemplates from './hcm/pages/Templates';
import TemplateEditor from './hcm/pages/TemplateEditor';
import OrgChart from './hcm/pages/OrgChart';
import PtoManager from './hcm/pages/PtoManager';
import OnboardingManager from './hcm/pages/OnboardingManager';
import HcmReports from './hcm/pages/Reports';
import Announcements from './hcm/pages/Announcements';
import ReviewManager from './hcm/pages/ReviewManager';

// ATS
import AtsDashboard from './ats/pages/Dashboard';
import AtsJobs from './ats/pages/Jobs';
import AtsJobDetail from './ats/pages/JobDetail';
import AtsApplicants from './ats/pages/Applicants';
import AtsApplicantDetail from './ats/pages/ApplicantDetail';
import AtsSettings from './ats/pages/Settings';
import AtsPipeline from './ats/pages/Pipeline';
import AtsInterviews from './ats/pages/Interviews';
import AtsReports from './ats/pages/Reports';

// Timesheets
import Timesheets from './timesheets/pages/Timesheets';
import AddTimesheet from './timesheets/pages/AddTimesheet';
import TimesheetDetail from './timesheets/pages/TimesheetDetail';
import EditTimesheet from './timesheets/pages/EditTimesheet';
import TimesheetSettings from './timesheets/pages/Settings';

// Banned List
import BannedList from './banned-list/pages/BannedList';
import AddBannedItem from './banned-list/pages/AddBannedItem';
import BannedItemDetail from './banned-list/pages/BannedItemDetail';
import EditBannedItem from './banned-list/pages/EditBannedItem';

// Documents
import Documents from './documents/pages/Documents';
import DocumentDetail from './documents/pages/DocumentDetail';
import DocumentEditor from './documents/pages/DocumentEditor';

// LMS
import PolicyDocuments from './policies/pages/Documents';
import PolicyDocumentEditor from './policies/pages/PolicyDocumentEditor';
import Courses from './lms/pages/Courses';
import CourseDetail from './lms/pages/CourseDetail';
import PageEditor from './lms/pages/PageEditor';
import MyCourses from './lms/pages/MyCourses';
import CourseViewer from './lms/pages/CourseViewer';

// Analytics
import AnalyticsDashboard from './analytics/pages/Dashboard';
import AnalyticsSites from './analytics/pages/Sites';
import AnalyticsSiteDetail from './analytics/pages/SiteDetail';
import AnalyticsCallTracking from './analytics/pages/CallTracking';
import AnalyticsShortLinks from './analytics/pages/ShortLinks';

// Receipts
import ReceiptsList from './receipts/pages/Receipts';
import ReceiptDetail from './receipts/pages/ReceiptDetail';

// Recordings
import RecordingsList from './recordings/pages/Recordings';
import RecordingDetail from './recordings/pages/RecordingDetail';

// Estimates
import EstimatesList from './estimates/pages/Estimates';
import AddEstimate from './estimates/pages/AddEstimate';
import EstimateDetail from './estimates/pages/EstimateDetail';
import EditEstimate from './estimates/pages/EditEstimate';

// Jobs
import JobsList from './jobs/pages/Jobs';
import AddJob from './jobs/pages/AddJob';
import JobDetail from './jobs/pages/JobDetail';
import EditJob from './jobs/pages/EditJob';
import JobBoard from './jobs/pages/JobBoard';

// Accounting
import AccountingDashboard from './accounting/pages/Dashboard';

// Calendar
import CalendarPage from './calendar/pages/Calendar';
import Accounts from './accounting/pages/Accounts';
import AddAccount from './accounting/pages/AddAccount';
import AccountDetail from './accounting/pages/AccountDetail';
import EditAccount from './accounting/pages/EditAccount';
import JournalEntries from './accounting/pages/JournalEntries';
import AddJournalEntry from './accounting/pages/AddJournalEntry';
import JournalEntryDetail from './accounting/pages/JournalEntryDetail';
import EditJournalEntry from './accounting/pages/EditJournalEntry';
import Invoices from './accounting/pages/Invoices';
import AddInvoice from './accounting/pages/AddInvoice';
import InvoiceDetail from './accounting/pages/InvoiceDetail';
import EditInvoice from './accounting/pages/EditInvoice';
import Bills from './accounting/pages/Bills';
import AddBill from './accounting/pages/AddBill';
import BillDetail from './accounting/pages/BillDetail';
import EditBill from './accounting/pages/EditBill';
import GeneralLedger from './accounting/pages/GeneralLedger';
import TrialBalance from './accounting/pages/TrialBalance';
import AccountingInfo from './accounting/pages/Info';
import RecurringTransactions from './accounting/pages/RecurringTransactions';
import BankReconciliations from './accounting/pages/BankReconciliations';
import AddBankReconciliation from './accounting/pages/AddBankReconciliation';
import BankReconciliationDetail from './accounting/pages/BankReconciliationDetail';
import BankFeeds from './accounting/pages/BankFeeds';

function LandingPage() {
  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      <Features />
      <Footer />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  return isTokenValid() ? children : <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
}

function TenantRequiredRoute({ children }) {
  const location = useLocation();
  if (!isTokenValid()) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  const hasTenant = document.cookie.split(';').some(c => c.trim().startsWith('tenantId='));
  if (!hasTenant) return <Navigate to={`/tenants?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function App() {
  return (
    <Router>
      <ConnectionBanner />
      <Routes>
        {/* Landing & Auth */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/tenants" element={<ProtectedRoute><TenantSelector /></ProtectedRoute>} />
        <Route path="/apps" element={<ProtectedRoute><AppSelector /></ProtectedRoute>} />
        <Route path="/settings" element={<TenantRequiredRoute><Settings /></TenantRequiredRoute>} />
        <Route path="/org-admin" element={<ProtectedRoute><OrgAdmin /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/tenants/:id" element={<ProtectedRoute><AdminTenantDetail /></ProtectedRoute>} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/policy/privacy" element={<PrivacyPolicy />} />


        {/* Redirects for old URLs */}
        <Route path="/boxes/*" element={<Navigate to={window.location.pathname.replace('/boxes', '/storage/boxes')} replace />} />

        {/* Storage */}
        <Route path="/storage" element={<TenantRequiredRoute><Boxes /></TenantRequiredRoute>} />
        <Route path="/storage/boxes" element={<TenantRequiredRoute><Boxes /></TenantRequiredRoute>} />
        <Route path="/storage/boxes/new" element={<TenantRequiredRoute><AddBox /></TenantRequiredRoute>} />
        <Route path="/storage/boxes/:id" element={<TenantRequiredRoute><BoxDetail /></TenantRequiredRoute>} />
        <Route path="/storage/boxes/:id/edit" element={<TenantRequiredRoute><EditBox /></TenantRequiredRoute>} />

        {/* Inventory */}
        <Route path="/inventory" element={<TenantRequiredRoute><InventoryDashboard /></TenantRequiredRoute>} />
        <Route path="/inventory/items" element={<TenantRequiredRoute><Items /></TenantRequiredRoute>} />
        <Route path="/inventory/items/new" element={<TenantRequiredRoute><AddItem /></TenantRequiredRoute>} />
        <Route path="/inventory/items/:id" element={<TenantRequiredRoute><ItemDetail /></TenantRequiredRoute>} />
        <Route path="/inventory/items/:id/edit" element={<TenantRequiredRoute><EditItem /></TenantRequiredRoute>} />
        <Route path="/inventory/items/:id/inventory/new" element={<TenantRequiredRoute><AddInventory /></TenantRequiredRoute>} />
        <Route path="/inventory/items/:id/inventory/:inventoryId/edit" element={<TenantRequiredRoute><EditInventory /></TenantRequiredRoute>} />
        <Route path="/inventory/orders" element={<TenantRequiredRoute><Orders /></TenantRequiredRoute>} />
        <Route path="/inventory/orders/new" element={<TenantRequiredRoute><AddOrder /></TenantRequiredRoute>} />
        <Route path="/inventory/orders/:id" element={<TenantRequiredRoute><OrderDetail /></TenantRequiredRoute>} />
        <Route path="/inventory/orders/:id/edit" element={<TenantRequiredRoute><EditOrder /></TenantRequiredRoute>} />

        {/* Vehicles */}
        <Route path="/vehicles" element={<TenantRequiredRoute><VehiclesList /></TenantRequiredRoute>} />
        <Route path="/vehicles/new" element={<TenantRequiredRoute><AddVehicle /></TenantRequiredRoute>} />
        <Route path="/vehicles/:id" element={<TenantRequiredRoute><VehicleDetail /></TenantRequiredRoute>} />
        <Route path="/vehicles/:id/edit" element={<TenantRequiredRoute><EditVehicle /></TenantRequiredRoute>} />
        <Route path="/vehicles/:id/service/new" element={<TenantRequiredRoute><AddServiceRecord /></TenantRequiredRoute>} />
        <Route path="/vehicles/:id/service/:recordId/edit" element={<TenantRequiredRoute><EditServiceRecord /></TenantRequiredRoute>} />
        <Route path="/vehicles/service" element={<TenantRequiredRoute><ServiceRecords /></TenantRequiredRoute>} />
        <Route path="/vehicles/settings" element={<TenantRequiredRoute><VehicleSettings /></TenantRequiredRoute>} />

        {/* Tasks */}
        <Route path="/tasks" element={<TenantRequiredRoute><TasksList /></TenantRequiredRoute>} />
        <Route path="/tasks/new" element={<TenantRequiredRoute><AddTask /></TenantRequiredRoute>} />
        <Route path="/tasks/:id" element={<TenantRequiredRoute><TaskDetail /></TenantRequiredRoute>} />
        <Route path="/tasks/:id/edit" element={<TenantRequiredRoute><EditTask /></TenantRequiredRoute>} />

        {/* CRM */}
        <Route path="/crm" element={<TenantRequiredRoute><CrmDashboard /></TenantRequiredRoute>} />
        <Route path="/crm/contacts" element={<TenantRequiredRoute><Contacts /></TenantRequiredRoute>} />
        <Route path="/crm/contacts/new" element={<TenantRequiredRoute><AddContact /></TenantRequiredRoute>} />
        <Route path="/crm/contacts/:id" element={<TenantRequiredRoute><ContactDetail /></TenantRequiredRoute>} />
        <Route path="/crm/contacts/:id/edit" element={<TenantRequiredRoute><EditContact /></TenantRequiredRoute>} />
        <Route path="/crm/deals" element={<TenantRequiredRoute><Deals /></TenantRequiredRoute>} />
        <Route path="/crm/deals/new" element={<TenantRequiredRoute><AddDeal /></TenantRequiredRoute>} />
        <Route path="/crm/deals/:id" element={<TenantRequiredRoute><DealDetail /></TenantRequiredRoute>} />
        <Route path="/crm/deals/:id/edit" element={<TenantRequiredRoute><EditDeal /></TenantRequiredRoute>} />
        <Route path="/crm/companies" element={<TenantRequiredRoute><Companies /></TenantRequiredRoute>} />
        <Route path="/crm/companies/new" element={<TenantRequiredRoute><AddCompany /></TenantRequiredRoute>} />
        <Route path="/crm/companies/:id" element={<TenantRequiredRoute><CompanyDetail /></TenantRequiredRoute>} />
        <Route path="/crm/companies/:id/edit" element={<TenantRequiredRoute><EditCompany /></TenantRequiredRoute>} />
        <Route path="/crm/tasks" element={<TenantRequiredRoute><CrmTasks /></TenantRequiredRoute>} />
        <Route path="/crm/tasks/new" element={<TenantRequiredRoute><AddCrmTask /></TenantRequiredRoute>} />
        <Route path="/crm/tasks/:id" element={<TenantRequiredRoute><CrmTaskDetail /></TenantRequiredRoute>} />
        <Route path="/crm/tasks/:id/edit" element={<TenantRequiredRoute><EditCrmTask /></TenantRequiredRoute>} />
        <Route path="/crm/settings" element={<TenantRequiredRoute><CrmSettings /></TenantRequiredRoute>} />
        <Route path="/crm/reports" element={<TenantRequiredRoute><CrmReports /></TenantRequiredRoute>} />

        {/* Marketing */}
        <Route path="/marketing" element={<TenantRequiredRoute><MarketingDashboard /></TenantRequiredRoute>} />
        <Route path="/marketing/campaigns" element={<TenantRequiredRoute><MarketingCampaigns /></TenantRequiredRoute>} />
        <Route path="/marketing/campaigns/new" element={<TenantRequiredRoute><AddCampaign /></TenantRequiredRoute>} />
        <Route path="/marketing/campaigns/:id" element={<TenantRequiredRoute><CampaignDetail /></TenantRequiredRoute>} />
        <Route path="/marketing/campaigns/:id/edit" element={<TenantRequiredRoute><EditCampaign /></TenantRequiredRoute>} />

        {/* HCM */}
        <Route path="/hcm" element={<TenantRequiredRoute><HcmDashboard /></TenantRequiredRoute>} />
        <Route path="/hcm/employees/new" element={<TenantRequiredRoute><AddEmployee /></TenantRequiredRoute>} />
        <Route path="/hcm/employees/:id" element={<TenantRequiredRoute><EmployeeDetail /></TenantRequiredRoute>} />
        <Route path="/hcm/employees/:employeeId/documents/:docId/edit" element={<TenantRequiredRoute><EmployeeDocumentEditor /></TenantRequiredRoute>} />
        <Route path="/hcm/employees/:id/edit" element={<TenantRequiredRoute><EditEmployee /></TenantRequiredRoute>} />
        <Route path="/hcm/settings" element={<TenantRequiredRoute><HcmSettings /></TenantRequiredRoute>} />
        <Route path="/hcm/templates" element={<TenantRequiredRoute><HcmTemplates /></TenantRequiredRoute>} />
        <Route path="/hcm/templates/:id/edit" element={<TenantRequiredRoute><TemplateEditor /></TenantRequiredRoute>} />
        <Route path="/hcm/org-chart" element={<TenantRequiredRoute><OrgChart /></TenantRequiredRoute>} />
        <Route path="/hcm/pto" element={<TenantRequiredRoute><PtoManager /></TenantRequiredRoute>} />
        <Route path="/hcm/onboarding" element={<TenantRequiredRoute><OnboardingManager /></TenantRequiredRoute>} />
        <Route path="/hcm/reports" element={<TenantRequiredRoute><HcmReports /></TenantRequiredRoute>} />
        <Route path="/hcm/announcements" element={<TenantRequiredRoute><Announcements /></TenantRequiredRoute>} />
        <Route path="/hcm/reviews" element={<TenantRequiredRoute><ReviewManager /></TenantRequiredRoute>} />

        {/* ATS */}
        <Route path="/ats" element={<TenantRequiredRoute><AtsDashboard /></TenantRequiredRoute>} />
        <Route path="/ats/jobs" element={<TenantRequiredRoute><AtsJobs /></TenantRequiredRoute>} />
        <Route path="/ats/jobs/:id" element={<TenantRequiredRoute><AtsJobDetail /></TenantRequiredRoute>} />
        <Route path="/ats/applicants" element={<TenantRequiredRoute><AtsApplicants /></TenantRequiredRoute>} />
        <Route path="/ats/applicants/:id" element={<TenantRequiredRoute><AtsApplicantDetail /></TenantRequiredRoute>} />
        <Route path="/ats/pipeline" element={<TenantRequiredRoute><AtsPipeline /></TenantRequiredRoute>} />
        <Route path="/ats/interviews" element={<TenantRequiredRoute><AtsInterviews /></TenantRequiredRoute>} />
        <Route path="/ats/reports" element={<TenantRequiredRoute><AtsReports /></TenantRequiredRoute>} />
        <Route path="/ats/settings" element={<TenantRequiredRoute><AtsSettings /></TenantRequiredRoute>} />

        {/* Timesheets */}
        <Route path="/timesheets" element={<TenantRequiredRoute><Timesheets /></TenantRequiredRoute>} />
        <Route path="/timesheets/new" element={<TenantRequiredRoute><AddTimesheet /></TenantRequiredRoute>} />
        <Route path="/timesheets/settings" element={<TenantRequiredRoute><TimesheetSettings /></TenantRequiredRoute>} />
        <Route path="/timesheets/:id" element={<TenantRequiredRoute><TimesheetDetail /></TenantRequiredRoute>} />
        <Route path="/timesheets/:id/edit" element={<TenantRequiredRoute><EditTimesheet /></TenantRequiredRoute>} />

        {/* Accounting */}
        <Route path="/accounting" element={<TenantRequiredRoute><AccountingDashboard /></TenantRequiredRoute>} />
        <Route path="/accounting/accounts" element={<TenantRequiredRoute><Accounts /></TenantRequiredRoute>} />
        <Route path="/accounting/accounts/new" element={<TenantRequiredRoute><AddAccount /></TenantRequiredRoute>} />
        <Route path="/accounting/accounts/:id" element={<TenantRequiredRoute><AccountDetail /></TenantRequiredRoute>} />
        <Route path="/accounting/accounts/:id/edit" element={<TenantRequiredRoute><EditAccount /></TenantRequiredRoute>} />
        <Route path="/accounting/journal-entries" element={<TenantRequiredRoute><JournalEntries /></TenantRequiredRoute>} />
        <Route path="/accounting/journal-entries/new" element={<TenantRequiredRoute><AddJournalEntry /></TenantRequiredRoute>} />
        <Route path="/accounting/journal-entries/:id" element={<TenantRequiredRoute><JournalEntryDetail /></TenantRequiredRoute>} />
        <Route path="/accounting/journal-entries/:id/edit" element={<TenantRequiredRoute><EditJournalEntry /></TenantRequiredRoute>} />
        <Route path="/accounting/invoices" element={<TenantRequiredRoute><Invoices /></TenantRequiredRoute>} />
        <Route path="/accounting/invoices/new" element={<TenantRequiredRoute><AddInvoice /></TenantRequiredRoute>} />
        <Route path="/accounting/invoices/:id" element={<TenantRequiredRoute><InvoiceDetail /></TenantRequiredRoute>} />
        <Route path="/accounting/invoices/:id/edit" element={<TenantRequiredRoute><EditInvoice /></TenantRequiredRoute>} />
        <Route path="/accounting/bills" element={<TenantRequiredRoute><Bills /></TenantRequiredRoute>} />
        <Route path="/accounting/bills/new" element={<TenantRequiredRoute><AddBill /></TenantRequiredRoute>} />
        <Route path="/accounting/bills/:id" element={<TenantRequiredRoute><BillDetail /></TenantRequiredRoute>} />
        <Route path="/accounting/bills/:id/edit" element={<TenantRequiredRoute><EditBill /></TenantRequiredRoute>} />
        <Route path="/accounting/reports/general-ledger" element={<TenantRequiredRoute><GeneralLedger /></TenantRequiredRoute>} />
        <Route path="/accounting/reports/trial-balance" element={<TenantRequiredRoute><TrialBalance /></TenantRequiredRoute>} />
        <Route path="/accounting/info" element={<TenantRequiredRoute><AccountingInfo /></TenantRequiredRoute>} />
        <Route path="/accounting/recurring" element={<TenantRequiredRoute><RecurringTransactions /></TenantRequiredRoute>} />
        <Route path="/accounting/bank-reconciliations" element={<TenantRequiredRoute><BankReconciliations /></TenantRequiredRoute>} />
        <Route path="/accounting/bank-reconciliations/new" element={<TenantRequiredRoute><AddBankReconciliation /></TenantRequiredRoute>} />
        <Route path="/accounting/bank-reconciliations/:id" element={<TenantRequiredRoute><BankReconciliationDetail /></TenantRequiredRoute>} />
        <Route path="/accounting/bank-feeds" element={<TenantRequiredRoute><BankFeeds /></TenantRequiredRoute>} />

        {/* Banned List */}
        <Route path="/banned-list" element={<TenantRequiredRoute><BannedList /></TenantRequiredRoute>} />
        <Route path="/banned-list/new" element={<TenantRequiredRoute><AddBannedItem /></TenantRequiredRoute>} />
        <Route path="/banned-list/:id" element={<TenantRequiredRoute><BannedItemDetail /></TenantRequiredRoute>} />
        <Route path="/banned-list/:id/edit" element={<TenantRequiredRoute><EditBannedItem /></TenantRequiredRoute>} />

        {/* Documents */}
        <Route path="/documents" element={<TenantRequiredRoute><Documents /></TenantRequiredRoute>} />
        <Route path="/documents/:id" element={<TenantRequiredRoute><DocumentDetail /></TenantRequiredRoute>} />
        <Route path="/documents/:id/edit" element={<TenantRequiredRoute><DocumentEditor /></TenantRequiredRoute>} />

        {/* LMS */}
        <Route path="/lms" element={<TenantRequiredRoute><Courses /></TenantRequiredRoute>} />
        <Route path="/lms/courses/:id" element={<TenantRequiredRoute><CourseDetail /></TenantRequiredRoute>} />
        <Route path="/lms/courses/:id/pages/:pageId" element={<TenantRequiredRoute><PageEditor /></TenantRequiredRoute>} />
        <Route path="/lms/my-courses" element={<TenantRequiredRoute><MyCourses /></TenantRequiredRoute>} />
        <Route path="/lms/my-courses/:courseId" element={<TenantRequiredRoute><CourseViewer /></TenantRequiredRoute>} />

        {/* Policies */}
        <Route path="/policies" element={<TenantRequiredRoute><PolicyDocuments /></TenantRequiredRoute>} />
        <Route path="/policies/documents/:id/edit" element={<TenantRequiredRoute><PolicyDocumentEditor /></TenantRequiredRoute>} />

        {/* Analytics */}
        <Route path="/analytics" element={<TenantRequiredRoute><AnalyticsDashboard /></TenantRequiredRoute>} />
        <Route path="/analytics/sites" element={<TenantRequiredRoute><AnalyticsSites /></TenantRequiredRoute>} />
        <Route path="/analytics/sites/:id" element={<TenantRequiredRoute><AnalyticsSiteDetail /></TenantRequiredRoute>} />
        <Route path="/analytics/calls" element={<TenantRequiredRoute><AnalyticsCallTracking /></TenantRequiredRoute>} />
        <Route path="/analytics/short-links" element={<TenantRequiredRoute><AnalyticsShortLinks /></TenantRequiredRoute>} />

        {/* Receipts */}
        <Route path="/receipts" element={<TenantRequiredRoute><ReceiptsList /></TenantRequiredRoute>} />
        <Route path="/receipts/:id" element={<TenantRequiredRoute><ReceiptDetail /></TenantRequiredRoute>} />

        {/* Recordings */}
        <Route path="/recordings" element={<TenantRequiredRoute><RecordingsList /></TenantRequiredRoute>} />
        <Route path="/recordings/:id" element={<TenantRequiredRoute><RecordingDetail /></TenantRequiredRoute>} />

        {/* Estimates */}
        <Route path="/estimates" element={<TenantRequiredRoute><EstimatesList /></TenantRequiredRoute>} />
        <Route path="/estimates/new" element={<TenantRequiredRoute><AddEstimate /></TenantRequiredRoute>} />
        <Route path="/estimates/:id" element={<TenantRequiredRoute><EstimateDetail /></TenantRequiredRoute>} />
        <Route path="/estimates/:id/edit" element={<TenantRequiredRoute><EditEstimate /></TenantRequiredRoute>} />

        {/* Jobs */}
        <Route path="/jobs" element={<TenantRequiredRoute><JobsList /></TenantRequiredRoute>} />
        <Route path="/jobs/board" element={<TenantRequiredRoute><JobBoard /></TenantRequiredRoute>} />
        <Route path="/jobs/new" element={<TenantRequiredRoute><AddJob /></TenantRequiredRoute>} />
        <Route path="/jobs/:id" element={<TenantRequiredRoute><JobDetail /></TenantRequiredRoute>} />
        <Route path="/jobs/:id/edit" element={<TenantRequiredRoute><EditJob /></TenantRequiredRoute>} />

        {/* Calendar */}
        <Route path="/calendar" element={<TenantRequiredRoute><CalendarPage /></TenantRequiredRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
